import { ProductResultDto } from 'src/dtos/productResult/ProductResultDto';
import { PRD3040_R01Dto, DateProductResultDto, ItemProductResultDto, WorkplaceProductResultDto } from '../Dto_PRD3040_R01';
import { PRD3040_R01TabType } from '../Store_PRD3040_R01';
import { formatDate } from 'src/utils/string/stringUtil';

// 소계 타입 정의
export const PRD3040_R01_SUBTOTAL_TYPES = {
    DATE: 'date',
    ITEM: 'item', 
    WORKPLACE: 'workplace',
} as const;

export type SubtotalType = typeof PRD3040_R01_SUBTOTAL_TYPES[keyof typeof PRD3040_R01_SUBTOTAL_TYPES];

// 소계 행 데이터 인터페이스
export interface PRD3040_R01SubtotalRow extends ProductResultDto {
    isSubtotal: true;
    subtotalType: SubtotalType;
    groupKey: string;
    groupLabel: string;
    count: number;
}

// 통합 그리드 데이터 타입
export type PRD3040_R01GridRowData = PRD3040_R01Dto | PRD3040_R01SubtotalRow;

// 소계 행 확인 헬퍼 함수들
export const PRD3040_R01isSubtotalRow = (row: PRD3040_R01GridRowData): row is PRD3040_R01SubtotalRow => {
    return 'isSubtotal' in row && row.isSubtotal === true;
};

export const PRD3040_R01isDateSubtotalRow = (row: PRD3040_R01GridRowData): row is PRD3040_R01SubtotalRow => {
    return PRD3040_R01isSubtotalRow(row) && row.subtotalType === PRD3040_R01_SUBTOTAL_TYPES.DATE;
};

export const PRD3040_R01isItemSubtotalRow = (row: PRD3040_R01GridRowData): row is PRD3040_R01SubtotalRow => {
    return PRD3040_R01isSubtotalRow(row) && row.subtotalType === PRD3040_R01_SUBTOTAL_TYPES.ITEM;
};

export const PRD3040_R01isWorkplaceSubtotalRow = (row: PRD3040_R01GridRowData): row is PRD3040_R01SubtotalRow => {
    return PRD3040_R01isSubtotalRow(row) && row.subtotalType === PRD3040_R01_SUBTOTAL_TYPES.WORKPLACE;
};

const PRD3040_R01toDateGroupKey = (value: Date | string | undefined): string => {
    if (!value) return '';

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return '';
        return value.toISOString().split('T')[0];
    }

    if (typeof value === 'string') {
        // 이미 YYYY-MM-DD 형태면 그대로 사용
        const normalized = value.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toISOString().split('T')[0];
        }
    }

    return '';
};

/**
 * 일자별 소계 계산 (일자별 탭용)
 */
export const calculateDateSubtotals = (data: DateProductResultDto[]): PRD3040_R01SubtotalRow[] => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
		const keyString = PRD3040_R01toDateGroupKey(item.productDt as Date | string | undefined);
        if (!keyString) return acc;

        if (!acc[keyString]) {
            acc[keyString] = {
				...item,
                isSubtotal: true as const,
                subtotalType: PRD3040_R01_SUBTOTAL_TYPES.DATE,
                groupKey: keyString,
                groupLabel: `${keyString} 소계`,
                count: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0,
                productDt: formatDate(new Date(keyString)),
            };
        }
        acc[keyString].count += 1;
        acc[keyString].orderQty += Number(item.orderQty) || 0;
        acc[keyString].productQty += Number(item.productQty) || 0;
        acc[keyString].badQty += Number(item.badQty) || 0;
        acc[keyString].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, PRD3040_R01SubtotalRow>);
    
    return Object.values(grouped).sort((a, b) => a.groupKey.localeCompare(b.groupKey));
};

/**
 * 품목별 소계 계산 (품목별 탭용)
 */
export const calculateItemSubtotals = (data: ItemProductResultDto[]): PRD3040_R01SubtotalRow[] => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
        const key = item.itemCd;
        if (!acc[key]) {
            acc[key] = {
				...item,
                isSubtotal: true as const,
                subtotalType: PRD3040_R01_SUBTOTAL_TYPES.ITEM,
                groupKey: key,
                groupLabel: `${item.itemNm || key} 소계`,
                count: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0,
                itemCd: key,
                itemNm: item.itemNm,
                itemSpec: item.itemSpec,
                itemUnit: item.itemUnit,
            };
        }
        acc[key].count += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, PRD3040_R01SubtotalRow>);
    
    return Object.values(grouped).sort((a, b) => a.groupKey.localeCompare(b.groupKey));
};

/**
 * 공정별 소계 계산 (공정별 탭용)
 */
export const calculateWorkplaceSubtotals = (data: WorkplaceProductResultDto[]): PRD3040_R01SubtotalRow[] => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
        const key = item.workplaceNm;
        if (!acc[key]) {
            acc[key] = {
				...item,
                isSubtotal: true as const,
                subtotalType: PRD3040_R01_SUBTOTAL_TYPES.WORKPLACE,
                groupKey: key,
                groupLabel: `${key} 소계`,
                count: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0,
                workplaceNm: key,
            };
        }
        acc[key].count += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, PRD3040_R01SubtotalRow>);
    
    return Object.values(grouped).sort((a, b) => a.groupKey.localeCompare(b.groupKey));
};

/**
 * 탭 타입에 따른 소계 계산 함수 매핑
 */
export const getSubtotalCalculator = (tabType: PRD3040_R01TabType) => {
    switch (tabType) {
        case 'date':
            return calculateDateSubtotals;
        case 'item':
            return calculateItemSubtotals;
        case 'workplace':
            return calculateWorkplaceSubtotals;
        default:
            return () => [];
    }
};

/**
 * 데이터와 소계를 병합하여 그리드 데이터 생성
 */
export const mergeDataWithSubtotals = <T extends PRD3040_R01Dto>(
    data: T[],
    subtotals: PRD3040_R01SubtotalRow[],
    showSubtotals: boolean,
    tabType?: PRD3040_R01TabType
): PRD3040_R01GridRowData[] => {
    if (!showSubtotals || subtotals.length === 0) {
        return data as PRD3040_R01GridRowData[];
    }
    
    const result: PRD3040_R01GridRowData[] = [];
    const subtotalMap = new Map(subtotals.map(s => [s.groupKey, s]));
    
    // 탭 타입에 따른 그룹핑 키 결정
    const getGroupKey = (item: any, tabType?: PRD3040_R01TabType) => {
        if (tabType === 'date' && item.productDt) return PRD3040_R01toDateGroupKey(item.productDt);
        if (tabType === 'item' && item.itemCd) return item.itemCd;
        if (tabType === 'workplace' && item.workplaceNm) return item.workplaceNm;
        
        // fallback: 자동 감지
        if (item.productDt) return PRD3040_R01toDateGroupKey(item.productDt);
        if (item.itemCd) return item.itemCd;
        if (item.workplaceNm) return item.workplaceNm;
        
        return '';
    };
    
    // 데이터를 그룹별로 정렬하고 소계 삽입
    const groupedData = data.reduce((acc, item) => {
        const groupKey = getGroupKey(item, tabType);
        
        if (!groupKey) {
            return acc;
        }
        
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(item);
        return acc;
    }, {} as Record<string, T[]>);
    
    // 그룹별로 데이터와 소계를 순서대로 배치 (데이터 먼저, 그 다음 소계)
    Object.keys(groupedData).sort().forEach(groupKey => {
        const groupData = groupedData[groupKey];
        const subtotal = subtotalMap.get(groupKey);
        
        // 먼저 데이터 추가
        result.push(...(groupData as PRD3040_R01GridRowData[]));
        
        // 그 다음 소계 추가
        if (subtotal) {
            result.push(subtotal);
        }
    });
    
    return result;
};

/**
 * 특정 그룹의 상세 행 ID 목록 반환
 */
export const getDetailRowIdsByGroup = (groupKey: string, tabType: PRD3040_R01TabType, gridData: PRD3040_R01GridRowData[]): string[] => {
    return gridData
        .filter(row => {
            if (PRD3040_R01isSubtotalRow(row)) return false;
            
            const rowData = row as any;
            switch (tabType) {
                case 'date':
                    return 'productDt' in row && rowData.productDt === groupKey;
                case 'item':
                    return 'itemCd' in row && rowData.itemCd === groupKey;
                case 'workplace':
                    return 'workplaceNm' in row && rowData.workplaceNm === groupKey;
                default:
                    return false;
            }
        })
        .map(row => {
            // 행 ID 생성 로직 (Table_PRD3040_R01의 getRowId와 동일)
            if ('productDt' in row) {
                return `${(row as any).compNm}-${(row as any).productDt}-${(row as any).workplaceNm}-${(row as any).itemCd}-${(row as any).productEmpNm}`;
            } else if ('itemCd' in row) {
                return `${(row as any).compNm}-${(row as any).itemCd}-${(row as any).workplaceNm}-${(row as any).productDt}-${(row as any).productEmpNm}`;
            } else if ('workplaceNm' in row) {
                return `${(row as any).compNm}-${(row as any).workplaceNm}-${(row as any).itemCd}-${(row as any).productDt}-${(row as any).productEmpNm}`;
            }
            return '';
        })
        .filter(id => id);
};
